/**
 * dynamic select 的首項（提示項）override 表——GNL-002（批次 29，Gitea issue #30）。
 *
 * 【來源】structure-diff 正向比對（.tmp/gnl002-select-map-v2.md）＋legacy raw-dom 每個差異
 * select 的首項原文（.tmp/gnl002-override-draft.txt）程式化萃取；寫死常數的 legacy 出處：
 * 批次 35（GNL-005-a，issue #37）補 11 條：R2 口徑（2026-09-30 react-only 重擷）option[0]
 * 缺文字，legacy 首項出處——請選擇("")：ePortfolio.js:722（doSetJobTypeElement
 * isAddFindAll=false）、:352（setDepartmentElementNoLimit）、:443（setDepartmentElementInner）；
 * 全部(-1)：ePortfolio.js:349（NoLimit）、:440（Inner）；呼叫點 formGenerator.js:35/41/66、
 * queryPage.js:38/48、studyItemMaintain.jsp:76/82、studyItemTypeMaintain.jsp:60/66、
 * teacherPoolList.jsp:36、leaveAnnual.jsp:30、leaveForm.js:263。
 * - 未設定(0)：accountV2.jsp:73 `$("#queryForm #jobTypeId").prepend("<option value='0'>未設定</option>")`
 * - 不包含次科室(0)：accountV2.jsp:152 `.append('<option value="0">不包含次科室</option>')`
 * - 共用表單(-999)：setFormTemplateSelectList（ePortfolio.js:907「加了共用表單 20180122」）
 * - 全部(-1)：ePortfolio.js:1328/1456/1560/1590/2043 prepend；請選擇(-1)：ePortfolio.js:81/257/526/593
 * - 請選擇("")：ePortfolio.js:212/352/443（new Option("請選擇", "")）
 *
 * 【裁決】GNL-002 拆半（使用者 2026-09-29）：寫死首項常數＝結構性，本表鏡像補齊；
 * DB 資料值差（本機帳號/系統管理/管理部門/115/角色清單/共用(0)）＝資料落差，另立
 * GNL-006 判 Approved Difference 結案（legacy 快照 DB 空資料 vs React mock 種子設計值）。
 *
 * 【為什麼是這些條】legacy 的首項是「DB 筆數的條件函式」（setOrganizationElement：
 * list.length>1 才 prepend 全部/請選擇，ePortfolio.js:1219-1254）；raw-dom 快照的 DB 只有
 * 1 筆（本機帳號），因此 organizationId 系 select 在 raw-dom 首項＝DB 值、無 prepend——
 * 那些屬 GNL-006 資料落差，不在本表。本表只收 raw-dom 證實的寫死首項。
 *
 * 【已知不收】
 * - 無資料(-2)：list.length==0 時 append（ePortfolio.js:925/1395、common.js:1194/1336）——
 *   mock 場景種子恆有資料、不觸發；React 空資料走 emptyText 模式。
 *   批次 52（裁定 E）勘註：凍結快照中 DB 清單為空時的初始態即 [-2=無資料] 單選項
 *   （form-plan-form-list¦formTemplateId、exam-dispatch-exam¦examTargetId、
 *   exam-testing-manage¦examTargetId、report-tsgh-academic-mom¦departmentId——
 *   【勘誤 b52-calibrate3 F-n6】初稿漏列第 4 鍵 exam-testing-manage¦examTargetId）
 *   ——已由 OPTION_SET_OVERRIDES（legacy-fields.ts）
 *   以凍結整組選項落地，非本表寫死 prepend 路徑；本表不收此類鍵的語意不變。
 * - NaN(-1)：vote 群 annualId 首項（vote/vote-dept-manager-recommend、vote-vote-maintenance）
 *   ＝legacy 年度運算 bug 的偶發產物（legacy-known-issue），本表不寫死鏡像；
 *   批次 52（裁定 G）勘註：兩頁 annualId 凍結選項集 [-1=NaN,1=115] 已由
 *   OPTION_SET_OVERRIDES 照凍結值校準（known-issue 本身不另修，僅鏡像凍結態）。
 * - course-course-static-list¦jobTypeId（-2 全部）：批次 24 已以 COURSE_STATIC_JOB_TYPE_OPTIONS
 *   首項鏡像（courseStaticList.jsp:112）。
 * - course-course-static-list¦departmentId：批次 24 已以 placeholder="請選擇" 鏡像（文字層）；
 *   legacy 請選擇(-1) 與 placeholder("") 的 value 語意差記錄結案（mock query 不真送 server）。
 */

export interface SelectPromptOverride {
  /** 首項 value（鏡像 legacy 寫死常數）。 */
  readonly value: string;
  /** 首項文字（全部／請選擇／未設定／不包含次科室／共用表單）。 */
  readonly text: string;
}

/** key ＝ `<pageId>¦<select name>`（pageId＝routes 短名，與 optionsFor/契約同口徑；已驗證無撞名）。 */
export const SELECT_PROMPT_OVERRIDES: Readonly<Record<string, SelectPromptOverride>> = {
  'announcement-announcement-list¦refId': { value: '-1', text: '全部' },
  'config-account-v-2¦jobTypeId': { value: '0', text: '未設定' },
  // W5（批次 42，issue #40）：帳號頁醫院選單的「未設定」——accountV2.jsp:615
  // `<option value="0">未設定</option>`（mainOrganizationList forEach 之後的靜態尾端選項；
  // raw-dom 快照 DB 僅本機帳號 1 筆→legacy 選項 [本機帳號(1), 未設定(0)]）。首項未設定
  // 的 value/text 是寫死結構常數（GNL-002 口徑）；首項本機帳號是 DB 資料值＝GNL-006 AD。
  'config-account-v-2¦organizationId': { value: '0', text: '未設定' },
  'config-account-v-2¦departmentId': { value: '-1', text: '全部' },
  'config-account-v-2¦secondDepartmentId': { value: '0', text: '不包含次科室' },
  'config-account-v-2¦roleId': { value: '0', text: '全部' },
  'config-device¦departmentId': { value: '-1', text: '全部' },
  'config-location¦departmentId': { value: '-1', text: '全部' },
  'course-clazz-query-list-class-query¦departmentId': { value: '-1', text: '全部' },
  'course-clazz-query-list-class-query¦courseTypeId': { value: '-1', text: '全部' },
  'course-course-manage¦departmentId': { value: '-1', text: '全部' },
  'course-course-manage¦courseTypeId': { value: '-1', text: '全部' },
  'course-course-type¦jobTypeId': { value: '', text: '請選擇' },
  'course-course-type¦departmentId': { value: '-1', text: '全部' },
  'form-class-form-list¦annualId': { value: '0', text: '全部' },
  'form-class-form-list¦jobTypeId': { value: '', text: '請選擇' },
  'form-class-form-list¦departmentId': { value: '-1', text: '全部' },
  'form-class-form-list¦courseTypeId': { value: '-1', text: '全部' },
  'form-class-form-list¦formDepartmentId': { value: '-999', text: '共用表單' },
  'report-common-class-form-export¦jobTypeId': { value: '', text: '請選擇' },
  'report-common-class-form-export¦departmentId': { value: '-1', text: '全部' },
  'report-common-class-form-export¦formDepartmentId': { value: '-999', text: '共用表單' },
  'discuss-discuss-board¦discussTypeId': { value: '0', text: '全部' },
  'exam-exam-manage¦departmentId': { value: '-1', text: '全部' },
  'exam-question-bank-type¦departmentId': { value: '-1', text: '全部' },
  'exam-question-bank¦departmentId': { value: '-1', text: '全部' },
  'config-comment¦departmentId': { value: '-1', text: '全部' },
  'form-form-static-list¦jobtypeId': { value: '-1', text: '請選擇' },
  'form-form-static-list¦departmentId': { value: '-1', text: '請選擇' },
  'report-common-clazz-attendance-export¦departmentId': { value: '-1', text: '全部' },
  'report-common-clazz-attendance-export¦courseTypeId': { value: '-1', text: '全部' },
  'report-common-clazz-detail-export¦departmentId': { value: '-1', text: '全部' },
  'report-common-clazz-detail-export¦courseTypeId': { value: '-1', text: '全部' },
  'report-tsgh-core-skill-form-report¦jobTypeId': { value: '', text: '請選擇' },
  'report-tsgh-core-skill-form-report¦departmentId': { value: '-1', text: '全部' },
  'report-tsgh-core-skill-form-report¦jobTypeId2': { value: '', text: '請選擇' },
  'report-tsgh-core-skill-form-report¦departmentId2': { value: '-1', text: '全部' },
  'report-tsgh-core-skill-statistic¦jobTypeId': { value: '', text: '請選擇' },
  'report-tsgh-core-skill-statistic¦departmentId': { value: '-1', text: '全部' },
  'report-tsgh-core-skill-statistic¦jobTypeId2': { value: '', text: '請選擇' },
  'report-tsgh-core-skill-statistic¦departmentId2': { value: '-1', text: '全部' },
  'report-tsgh-core-skill-sutdent-report¦jobTypeId': { value: '', text: '請選擇' },
  'report-tsgh-core-skill-sutdent-report¦departmentId': { value: '-1', text: '全部' },
  'report-tsgh-core-skill-sutdent-report¦jobTypeId2': { value: '', text: '請選擇' },
  'report-tsgh-core-skill-sutdent-report¦departmentId2': { value: '-1', text: '全部' },
  'report-tsgh-plan-form-static-2-to-plan-form-10¦phaseDepartmentId': { value: '', text: '全部' },
  'report-tsgh-plan-form-static-2-to-plan-form-9¦phaseDepartmentId': { value: '', text: '全部' },
  'report-tsgh-plan-form-static-2¦phaseDepartmentId': { value: '', text: '全部' },
  'report-ugywm-course-form-finishrate¦departmentId': { value: '-1', text: '全部' },
  'training-study-record-study-record-fill¦jobTypeId': { value: '', text: '請選擇' },
  'training-study-record-study-record-status¦jobTypeId': { value: '', text: '請選擇' },
  'customer-tsgh-case-report¦jobTypeId': { value: '', text: '請選擇' },
  'customer-tsgh-clert-cerk¦jobTypeId': { value: '', text: '請選擇' },
  'customer-tsgh-core-curriculum-report¦jobTypeId': { value: '', text: '請選擇' },
  'customer-tsgh-course-record-report¦jobTypeId': { value: '', text: '請選擇' },
  'customer-tsgh-tsgh-student-course-report¦jobTypeId': { value: '', text: '請選擇' },
  'form-all-form-list¦jobtypeId': { value: '-1', text: '請選擇' },
  'form-all-form-list¦departmentId': { value: '-1', text: '請選擇' },
  'form-plan-form-batch-send¦jobTypeId': { value: '', text: '請選擇' },
  'form-plan-form-list¦jobTypeId': { value: '', text: '請選擇' },
  'form-plan-form-list¦formDepartmentId': { value: '-999', text: '共用表單' },
  'report-common-plan-form-export¦jobTypeId': { value: '', text: '請選擇' },
  'report-common-plan-form-export¦departmentId': { value: '-1', text: '全部' },
  'report-common-plan-form-export¦formDepartmentId': { value: '-999', text: '共用表單' },
  'training-arrange-train¦jobTypeId': { value: '', text: '請選擇' },
  'training-permission-manage-student¦jobTypeId': { value: '', text: '請選擇' },
  'training-permission-manage-student¦departmentId': { value: '-1', text: '全部' },
  'training-permission-manage-student¦roleId': { value: '-1', text: '全部' },
  'training-plan-inst-plan-inst-list¦jobTypeId': { value: '', text: '請選擇' },
  'training-transcript-list¦jobTypeId': { value: '', text: '請選擇' },
  'training-transcript-list¦clinicalDeptId': { value: '-1', text: '全部' },
  'customer-tsgh-high-risk-doctor-report¦departmentId': { value: '0', text: '全部' },
  'customer-tsgh-high-risk-report¦departmentId': { value: '0', text: '全部' },
  'customer-tsgh-high-risk-setting-report¦departmentId': { value: '-1', text: '全部' },
  'customer-tsgh-high-risk-setting¦departmentId': { value: '-1', text: '請選擇' },
  'customer-tsgh-service-standard-cfg-report¦departmentId': { value: '0', text: '全部' },
  'customer-tsgh-service-standard-report¦departmentId': { value: '0', text: '全部' },
  'customer-tsgh-service-standard-setting¦departmentId': { value: '-1', text: '請選擇' },
  'training-permission-manage-teacher¦jobTypeId': { value: '', text: '請選擇' },
  'training-permission-manage-teacher¦departmentId': { value: '-1', text: '全部' },
  'training-permission-manage-teacher¦roleId': { value: '-1', text: '全部' },
  'training-plan-inst-clinical-arrange¦jobTypeId': { value: '-1', text: '請選擇' },
  'training-plan-inst-clinical-arrange¦clinicalDepartmentId': { value: '-1', text: '請選擇' },

  // ── 批次 35（GNL-005-a，issue #37）：R2 口徑 option[0] 缺文字補齊（11 條）──
  // 請選擇("")＝doSetJobTypeElement/setDepartmentElement* isAddFindAll=false 分支；
  // 全部(-1)＝setDepartmentElement* isAddFindAll=true 分支（ePortfolio.js:349/352/440/443/722）。
  'form-co-edit-form-form-generator¦jobType': { value: '', text: '請選擇' },
  'form-co-edit-form-form-generator¦formDeptId': { value: '', text: '請選擇' },
  'form-co-edit-form-query-page¦jobType': { value: '', text: '請選擇' },
  'form-co-edit-form-query-page¦formDepartment': { value: '-1', text: '全部' },
  'instruction-study-item-maintain¦jobTypeId': { value: '', text: '請選擇' },
  'instruction-study-item-maintain¦departmentId': { value: '-1', text: '全部' },
  'instruction-study-item-type-maintain¦jobTypeId': { value: '', text: '請選擇' },
  'instruction-study-item-type-maintain¦departmentId': { value: '-1', text: '全部' },
  'instruction-teacher-pool-list¦departmentId': { value: '-1', text: '全部' },
  'leave-leave-annual¦jobTypeId': { value: '', text: '請選擇' },
  'leave-leave-form¦jobTypeId': { value: '', text: '請選擇' },
};

/** 查某頁某 dynamic select 的首項 override；未命中回 undefined（維持既有空提示項）。 */
export function selectPromptFor(pageId: string, name: string): SelectPromptOverride | undefined {
  return SELECT_PROMPT_OVERRIDES[`${pageId}¦${name}`];
}
